import { RadioArray } from "controls/radioArray";
import { observer } from "mobx-react-lite";
import { SignatureType } from "models/notice";
import React, { FunctionComponent } from "react";
import { Form } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import ExemptionViewModel from "viewModels/notice/exemption/exemptionViewModel";
import OmissionViewModel from "viewModels/notice/omission/omissionViewModel";
import LicensePlateChangeViewModel from "../../../../viewModels/notice/licensePlateChange/licensePlateChangeViewModel";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import RefundRequestViewModel from "../../../../viewModels/notice/refundRequest/refundRequestViewModel";

const MethodOfSignature: FunctionComponent<{
  vm: ExemptionViewModel | OmissionViewModel | LicensePlateChangeViewModel | RefundRequestViewModel;
}> = observer(({ vm }) => {
  const target = vm.notice;
  let tg;
  let description;
  let requiredDocuments = "";
  if (vm instanceof LicensePlateChangeViewModel) {
    tg = (code: string) => vm.localizationService.translateGeneral(`submission.${code}`);
    requiredDocuments = tg("description");
    description = vm.localizationService.translateGeneral(`signature.description.${target._signatureType}`);
  } else if (vm instanceof RefundRequestViewModel) {
    tg = (code: string) => vm.localizationService.translateGeneral(`submission.${code}`);
    requiredDocuments = tg("refundRequestDescription").replace("%link%", vm.termsAgreementDocumentPath);
    description = vm.localizationService.translateGeneral(`signature.description.${target._signatureType}`);
  } else if (vm instanceof ExemptionViewModel) {
    tg = (code: string) => vm.localizationService.translateGeneral(`submission.${code}`);
    description = vm.localizationService.translateGeneral(`signature.description.${target._signatureType}`);
    if ([SignatureType.ElectronicSignature, SignatureType.DataBox].includes(target._signatureType)) {
      requiredDocuments = tg("exemptionRequestDescription");
    }
    if ([SignatureType.OfficiallyVerifiedSignature].includes(target._signatureType)) {
      description = vm.localizationService.translateGeneral(`signature.description.OFFICIALLY_VERIFIED_SELF_SIGNATURE`);
      requiredDocuments = tg("exemptionVerifiedSignatureDescription");
    }
  } else {
    tg = (code: string) => vm.localizationService.translateGeneral(`submission.${code}`);
    description = vm.localizationService.translateGeneral(`signature.description.${target._signatureType}`);
    if ([SignatureType.ElectronicSignature, SignatureType.DataBox].includes(target._signatureType)) {
      requiredDocuments = tg("omissionRequestDescription");
    }
    if ([SignatureType.OfficiallyVerifiedSignature].includes(target._signatureType)) {
      requiredDocuments = tg("omissionVerifiedSignatureDescription");
      description = vm.localizationService.translateGeneral(`signature.description.OFFICIALLY_VERIFIED_SELF_SIGNATURE`);
    }
  }

  return (
    <fieldset>
      <legend className="h3">{tg("title")}</legend>
      <>
        <Form.Group className="mb-3">
          <RadioArray
            custom
            label={<ReactMarkdown className="markdown">{tg("electronic")}</ReactMarkdown>}
            target={target}
            property="_signatureType"
            isNumeric={false}
            id={SignatureType.ElectronicSignature}
            option={SignatureType.ElectronicSignature}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <RadioArray
            custom
            label={<ReactMarkdown className="markdown">{tg("dataBox")}</ReactMarkdown>}
            target={target}
            property="_signatureType"
            disabled={vm.isdsHasActiveOutage}
            isNumeric={false}
            id={SignatureType.DataBox}
            option={SignatureType.DataBox}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <RadioArray
            custom
            label={<ReactMarkdown className="markdown">{tg("officiallyVerified")}</ReactMarkdown>}
            target={target}
            property="_signatureType"
            isNumeric={false}
            id={SignatureType.OfficiallyVerifiedSignature}
            option={SignatureType.OfficiallyVerifiedSignature}
          />
        </Form.Group>
      </>
      {requiredDocuments.length > 0 && (
        <div className="mt-4 mb-4 text-muted">
          <AlertWithInfoIcon variant="light">
            <ReactMarkdown linkTarget="_blank" className="markdown">
              {requiredDocuments}
            </ReactMarkdown>
          </AlertWithInfoIcon>
        </div>
      )}
      <div className="mt-4 mb-7 text-muted">
        <AlertWithInfoIcon variant="light">
          <ReactMarkdown linkTarget="_blank" className="markdown">
            {description}
          </ReactMarkdown>
        </AlertWithInfoIcon>
      </div>
    </fieldset>
  );
});

export default MethodOfSignature;
